import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/axiosConfig';

const ENDPOINT = '/api/admin/cupons';

const mensagemDeErro = (err) => err.response?.data?.mensagem || err.response?.data?.error || err.message;

// <input type="date"> gives a local day; the coupon stops working at the end of it (Brasília time).
const fimDoDia = (data) => (data ? new Date(`${data}T23:59:59.999-03:00`).toISOString() : null);
const paraInputDate = (iso) => (iso ? new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }) : '');

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : 'Sem prazo');

const situacao = (cupom) => {
  if (!cupom.ativo) return ['Desativado', 'bg-secondary'];
  if (cupom.validoAte && new Date(cupom.validoAte) <= new Date()) return ['Expirado', 'bg-secondary'];
  if (cupom.limiteUsos && cupom.usos >= cupom.limiteUsos) return ['Esgotado', 'bg-warning'];
  return ['Ativo', 'bg-success'];
};

const VAZIO = { codigo: '', percentual: '', descricao: '', validoAte: '', limiteUsos: '' };

const CuponsPage = () => {
  const [cupons, setCupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(VAZIO);
  const [submitting, setSubmitting] = useState(false);

  const fetchCupons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(ENDPOINT);
      setCupons(response.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar cupons: ' + mensagemDeErro(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCupons();
  }, [fetchCupons]);

  const avisar = (texto) => {
    setSuccess(texto);
    setTimeout(() => setSuccess(''), 3000);
  };

  const campo = (nome) => (e) => setForm((atual) => ({ ...atual, [nome]: e.target.value }));

  const handleCriar = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      await api.post(ENDPOINT, {
        codigo: form.codigo.trim(),
        percentual: Number(form.percentual),
        descricao: form.descricao.trim() || null,
        validoAte: fimDoDia(form.validoAte),
        limiteUsos: form.limiteUsos ? Number(form.limiteUsos) : null,
      });
      setForm(VAZIO);
      avisar('Cupom emitido.');
      await fetchCupons();
    } catch (err) {
      setError('Erro ao emitir cupom: ' + mensagemDeErro(err));
    } finally {
      setSubmitting(false);
    }
  };

  const atualizar = async (cupom, alteracoes, mensagem) => {
    try {
      setError('');
      await api.put(`${ENDPOINT}/${cupom.id}`, {
        descricao: cupom.descricao,
        percentual: cupom.percentual,
        validoAte: cupom.validoAte,
        limiteUsos: cupom.limiteUsos,
        ativo: cupom.ativo,
        ...alteracoes,
      });
      avisar(mensagem);
      await fetchCupons();
    } catch (err) {
      setError('Erro ao alterar cupom: ' + mensagemDeErro(err));
    }
  };

  const handleProrrogar = (cupom) => {
    const nova = window.prompt(`Nova validade de ${cupom.codigo} (AAAA-MM-DD; vazio = sem prazo):`, paraInputDate(cupom.validoAte));
    if (nova === null) return;
    if (nova && !/^\d{4}-\d{2}-\d{2}$/.test(nova.trim())) {
      setError('Data inválida: use o formato AAAA-MM-DD.');
      return;
    }
    atualizar(cupom, { validoAte: fimDoDia(nova.trim()) }, 'Validade alterada.');
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="mp-page-intro mb-4">
            <p className="eyebrow">Administração</p>
            <h1>Cupons de desconto</h1>
            <p>
              Códigos que o usuário informa ao assinar no aplicativo para ganhar um desconto percentual. O desconto vale
              em todas as cobranças daquela assinatura. Alterar ou desativar um cupom não muda o valor de quem já assinou.
            </p>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError('')}></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
            </div>
          )}

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Emitir cupom</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleCriar}>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label htmlFor="cupom-codigo" className="form-label">Código</label>
                    <input
                      id="cupom-codigo"
                      type="text"
                      className="form-control text-uppercase"
                      value={form.codigo}
                      onChange={campo('codigo')}
                      placeholder="PROMO20"
                      pattern="[A-Za-z0-9_\-]{3,40}"
                      title="De 3 a 40 letras, dígitos, _ ou -"
                      required
                    />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cupom-percentual" className="form-label">Desconto (%)</label>
                    <input
                      id="cupom-percentual"
                      type="number"
                      className="form-control"
                      value={form.percentual}
                      onChange={campo('percentual')}
                      min="1"
                      max="99"
                      required
                    />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cupom-validade" className="form-label">Válido até (opcional)</label>
                    <input
                      id="cupom-validade"
                      type="date"
                      className="form-control"
                      value={form.validoAte}
                      onChange={campo('validoAte')}
                    />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="cupom-limite" className="form-label">Limite de usos (opcional)</label>
                    <input
                      id="cupom-limite"
                      type="number"
                      className="form-control"
                      value={form.limiteUsos}
                      onChange={campo('limiteUsos')}
                      min="1"
                      placeholder="Ilimitado"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="cupom-descricao" className="form-label">Descrição (opcional)</label>
                    <input
                      id="cupom-descricao"
                      type="text"
                      className="form-control"
                      value={form.descricao}
                      onChange={campo('descricao')}
                      maxLength={500}
                      placeholder="Ex: campanha de lançamento"
                    />
                  </div>
                  <div className="col-12 d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Emitindo...' : 'Emitir cupom'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-header gap-2">
              <h5 className="mb-0">Cupons emitidos</h5>
              <span className="badge bg-primary">{cupons.length}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : cupons.length === 0 ? (
                <div className="text-center py-3 text-muted">Nenhum cupom emitido.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Desconto</th>
                        <th>Usos</th>
                        <th>Válido até</th>
                        <th>Situação</th>
                        <th>Descrição</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cupons.map((cupom) => {
                        const [rotulo, cor] = situacao(cupom);
                        return (
                          <tr key={cupom.id}>
                            <td><strong>{cupom.codigo}</strong></td>
                            <td>{cupom.percentual}%</td>
                            <td>{cupom.usos}{cupom.limiteUsos ? ` / ${cupom.limiteUsos}` : ''}</td>
                            <td>{formatDate(cupom.validoAte)}</td>
                            <td><span className={`badge ${cor}`}>{rotulo}</span></td>
                            <td><small className="text-muted">{cupom.descricao || '-'}</small></td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-outline-secondary" onClick={() => handleProrrogar(cupom)}>
                                  Validade
                                </button>
                                {cupom.ativo ? (
                                  <button
                                    className="btn btn-outline-warning"
                                    onClick={() => atualizar(cupom, { ativo: false }, 'Cupom desativado.')}
                                  >
                                    Desativar
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-outline-success"
                                    onClick={() => atualizar(cupom, { ativo: true }, 'Cupom reativado.')}
                                  >
                                    Reativar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CuponsPage;
